import { useState, useEffect } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import api, { formatDate } from "../lib/api";
import {
  PRIVILEGED_TARGET_ROLES,
  credentialIsDiscarded as isCredentialDiscarded,
} from "../lib/privilegedRoleProvisioning";
import { toast } from "sonner";
import { 
  Users, 
  Search,
  Copy,
  CheckCircle2,
  XCircle,
  Plus,
  UserPlus,
  Eye,
  EyeOff,
  KeyRound,
  Shield,
  MoreVertical,
  Layers,
  Mail as MailIcon,
  Trash2,
  Pencil
} from "lucide-react";
import ModuleAccessDialog from "../components/ModuleAccessDialog";
import ProvisionMailboxDialog from "../components/ProvisionMailboxDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Password reset state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetStage, setResetStage] = useState("init"); // "init" | "verify"
  const [resetToken, setResetToken] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetChannels, setResetChannels] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Module access dialog
  const [moduleAccessOpen, setModuleAccessOpen] = useState(false);
  const [moduleAccessUser, setModuleAccessUser] = useState(null);
  const openModuleAccessDialog = (u) => {
    setModuleAccessUser(u);
    setModuleAccessOpen(true);
  };

  // Mailbox provisioning dialog
  const [mailboxOpen, setMailboxOpen] = useState(false);
  const [mailboxUserId, setMailboxUserId] = useState(null);
  const openMailboxDialog = (u) => {
    setMailboxUserId(u.id);
    setMailboxOpen(true);
  };
  
  // Role change state
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [roleUser, setRoleUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [changingRole, setChangingRole] = useState(false);

  // forge-20260616-003: admin edit of a user's name / phone / email
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: "", phone: "", email: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: "",
    role: "client",
    referral_code: "",
    kyc_status: "pending",
    registration_paid: false,
    membership_active: false
  });

  useEffect(() => {
    fetchUsers();
    // `fetchUsers` is a stable in-component fn — mount-only fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // superuser-lifecycle-001: a superuser can deactivate / queue ANY account —
  // including other superusers — except their own; a plain admin cannot touch
  // admin/superuser rows. Mirrors the backend guard (_guard_lifecycle in
  // routes/users.py), which also refuses the seeded operator accounts.
  // Seeded operator accounts the backend refuses lifecycle verbs on for every
  // caller (mirror of _PURGE_PROTECTED_EMAILS in capital-markets-api
  // routes/users.py). Kept in sync by hand — the API is the source of truth;
  // this only avoids offering a control that's guaranteed to 400.
  const SEED_PROTECTED_EMAILS = new Set([
    "admin@echosensecapital.com",
    "super@echosensecapital.com",
    "rrr@echosensecapital.com",
    "founder@echosensecapital.com",
    "kevin@tryant.ai",
  ]);

  // Returns false while the auth context is still loading (user null) so the
  // controls never flash enabled before the viewer's role is known.
  //
  // `PRIVILEGED_TARGET_ROLES` now lives in `lib/privilegedRoleProvisioning` so the two
  // mirrors of the API's rules can be asserted on directly — both have drifted once,
  // and a constant inlined here cannot be tested without jsdom and a Radix select.
  //
  // backend guard: never self, never a seeded-floor account, and privileged targets
  // only for a superuser viewer.
  const canManageLifecycle = (u) =>
    !!user &&
    u.id !== user.id &&
    !SEED_PROTECTED_EMAILS.has((u.email || "").trim().toLowerCase()) &&
    (user.role === "superuser" || !PRIVILEGED_TARGET_ROLES.has(u.role));

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}/status?is_active=${!currentStatus}`);
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update user status");
    }
  };

  const updateKYCStatus = async (userId, status) => {
    try {
      await api.put(`/users/${userId}/kyc-status?status=${status}`);
      toast.success("KYC status updated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update KYC status");
    }
  };

  // forge-20260616-003: open/save the admin edit-details dialog
  const openEditDialog = (u) => {
    setEditUser(u);
    setEditForm({ full_name: u.full_name || "", phone: u.phone || "", email: u.email || "" });
    setShowEditDialog(true);
  };

  const saveProfileEdit = async () => {
    if (!editUser) return;
    const payload = {};
    if (editForm.full_name.trim() !== (editUser.full_name || "")) payload.full_name = editForm.full_name.trim();
    if (editForm.phone.trim() !== (editUser.phone || "")) payload.phone = editForm.phone.trim();
    if (editForm.email.trim().toLowerCase() !== (editUser.email || "").toLowerCase()) payload.email = editForm.email.trim();
    if (Object.keys(payload).length === 0) {
      setShowEditDialog(false);
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/users/${editUser.id}/profile`, payload);
      toast.success("User details updated");
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update details");
    } finally {
      setSavingEdit(false);
    }
  };

  // forge-20260615-007: queue/unqueue a user for the cascade purge. Marking also
  // disables login (backend). The actual hard-delete is a separate superuser step.
  const toggleDeletionFlag = async (u) => {
    if (!u.marked_for_deletion) {
      const reason = window.prompt(
        `Mark ${u.email} for deletion?\n\nThis DISABLES login immediately and queues the account for the admin purge (a separate, superuser-only step). Reversible until purged.\n\nOptional reason:`
      );
      if (reason === null) return; // cancelled
      try {
        await api.put(`/users/${u.id}/deletion-flag`, { marked: true, reason: reason || null });
        toast.success("Marked for deletion (login disabled)");
        fetchUsers();
      } catch (error) {
        toast.error(error?.response?.data?.detail || "Failed to mark for deletion");
      }
    } else {
      try {
        await api.put(`/users/${u.id}/deletion-flag`, { marked: false });
        toast.success("Deletion flag cleared (re-enable login separately)");
        fetchUsers();
      } catch (error) {
        toast.error("Failed to clear deletion flag");
      }
    }
  };

  const copyReferralCode = async (code) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = code;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (e) { console.debug("UsersPage: execCommand copy failed", e); }
        document.body.removeChild(ta);
      }
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      // Clipboard fallback failed (insecure context). Log for visibility.
      console.debug("UsersPage: clipboard copy fallback failed", err);
    }
  };

  // When true the API discards whatever password is sent and creates the row inactive,
  // so asking for one and then reporting plain success is a lie the operator acts on —
  // they hand over a password that cannot work (CAPMARKETS-283 review).
  const credentialIsDiscarded = isCredentialDiscarded(newUser.role, user?.role);

  const handleCreateUser = async (e) => {
    e.preventDefault();

    // `user` is null while the auth context loads, and `credentialIsDiscarded` reads an
    // unknown viewer as non-superuser — the safe default for what the form SHOWS, but
    // the wrong thing to submit on: a superuser who clicks quickly would send no
    // password, and the API, seeing a real superuser token, would then require one and
    // refuse with a 422. Block until the viewer is known rather than guessing
    // (CAPMARKETS-283 review, round five).
    if (!user?.role) {
      toast.error("Still loading your account — try again in a moment");
      return;
    }

    if (!newUser.email || !newUser.full_name ||
        (!credentialIsDiscarded && !newUser.password)) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!credentialIsDiscarded && newUser.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setCreating(true);
    try {
      // Omit the password entirely when the API will discard it, rather than sending
      // the empty string the disabled field holds. Copilot caught that on the review of
      // this change: `""` failed the schema's min-length and broke partner creation
      // from the console, for exactly the flow CAPMARKETS-283 is about.
      const payload = { ...newUser };
      if (credentialIsDiscarded) delete payload.password;
      await api.post("/users/create", payload);
      const roleLabel = newUser.role === "client" ? "Client" : newUser.role === "partner" ? "Partner" : "Referrer";
      if (credentialIsDiscarded) {
        toast.success(
          `${roleLabel} created. No password was set and the account is inactive — ` +
          `a superuser must activate it, then the holder sets their own password.`,
          { duration: 8000 },
        );
      } else {
        toast.success(`${roleLabel} created successfully`);
      }
      setShowAddDialog(false);
      setNewUser({
        email: "",
        password: "",
        full_name: "",
        phone: "",
        role: "client",
        referral_code: "",
        kyc_status: "pending",
        registration_paid: false,
        membership_active: false
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  // Password reset handler — 2FA flow
  // Step 1: request code (sent to admin's email + SMS)
  // Step 2: verify code + apply new password
  const handleResetRequest = async () => {
    setResetting(true);
    try {
      const r = await api.post("/admin/password-reset/request", {
        target_user_id: resetUser.id,
        delivery: "both",
      });
      setResetToken(r.data.reset_token);
      setResetChannels(r.data.channels_sent || []);
      setResetStage("verify");
      toast.success(`Code sent via ${r.data.channels_sent.join(", ")}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send code");
    } finally {
      setResetting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!resetCode || resetCode.length !== 6) {
      toast.error("Enter the 6-digit code from your email/SMS");
      return;
    }

    setResetting(true);
    try {
      await api.post("/admin/password-reset/confirm", {
        reset_token: resetToken,
        code: resetCode,
        new_password: newPassword,
      });
      toast.success(`Password reset for ${resetUser.email}`);
      setShowResetDialog(false);
      setResetUser(null);
      setNewPassword("");
      setResetCode("");
      setResetToken("");
      setResetStage("init");
      setResetChannels([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  // Role change handler
  const handleRoleChange = async () => {
    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    setChangingRole(true);
    try {
      await api.put(`/users/${roleUser.id}/role?new_role=${selectedRole}`);
      toast.success(`Role updated to ${selectedRole} for ${roleUser.email}`);
      setShowRoleDialog(false);
      setRoleUser(null);
      setSelectedRole("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setChangingRole(false);
    }
  };

  // Manual qualification (Paytiko-optional fallback)
  const handleMarkQualified = async (u) => {
    if (u.kyc_status !== "approved") {
      toast.error("KYC must be approved before qualifying");
      return;
    }
    try {
      await api.post(`/qualification/mark-qualified/${u.id}?transfer_amount=0`);
      toast.success(`${u.full_name || u.email} marked as qualified`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as qualified");
    }
  };

  // Open reset password dialog
  const openResetDialog = (u) => {
    setResetUser(u);
    setNewPassword("");
    setShowNewPassword(false);
    setShowResetDialog(true);
  };

  // Open role change dialog
  const openRoleDialog = (u) => {
    setRoleUser(u);
    setSelectedRole(u.role);
    setShowRoleDialog(true);
  };

  const filteredUsers = users.filter(u => 
    u.full_name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.referral_token?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout 
      title="User Management" 
      subtitle="View and manage all platform users"
    >
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-surface border-white/5">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-900">{users.length}</div>
            <p className="text-gray-500 text-sm">Total Users</p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/5">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-emerald">{users.filter(u => u.is_active).length}</div>
            <p className="text-gray-500 text-sm">Active Users</p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/5">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gold">{users.filter(u => u.kyc_status === 'approved').length}</div>
            <p className="text-gray-500 text-sm">KYC Verified</p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/5">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-400">{users.filter(u => u.registration_paid).length}</div>
            <p className="text-gray-500 text-sm">Paid Members</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-surface border-white/5 mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <Input
              placeholder="Search by name, email, or referral code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-900"
              data-testid="user-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-surface border-white/5">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <Users className="text-gold" size={20} />
              All Users ({filteredUsers.length})
            </CardTitle>
          </div>
          
          {/* Add User Button */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gold hover:bg-gold/90 text-navy-dark" data-testid="add-user-button">
                <UserPlus size={16} className="mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-surface border-white/10 max-w-md" aria-describedby="add-user-description">
              <DialogHeader>
                <DialogTitle className="text-white font-serif">Add New User</DialogTitle>
                <p id="add-user-description" className="text-gray-400 text-sm">Create a new client or referrer account</p>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Full Name *</Label>
                  <Input
                    value={newUser.full_name}
                    onChange={(e) => setNewUser(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="John Doe"
                    className="bg-white border-slate-200 text-slate-900"
                    required
                    data-testid="new-user-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Email *</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="user@example.com"
                    className="bg-white border-slate-200 text-slate-900"
                    required
                    data-testid="new-user-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">
                    {credentialIsDiscarded ? "Password (not used for this role)" : "Password *"}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={credentialIsDiscarded ? "" : newUser.password}
                      onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                      placeholder={credentialIsDiscarded
                        ? "Set by the account holder after activation"
                        : "Min 8 characters"}
                      className="bg-white border-slate-200 text-slate-900 pr-10"
                      required={!credentialIsDiscarded}
                      disabled={credentialIsDiscarded}
                      data-testid="new-user-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Phone (Optional)</Label>
                  <Input
                    value={newUser.phone}
                    onChange={(e) => setNewUser(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1 (555) 000-0000"
                    className="bg-white border-slate-200 text-slate-900"
                    data-testid="new-user-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Account Type *</Label>
                  <Select 
                    value={newUser.role} 
                    onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}
                  >
                    <SelectTrigger className="bg-surface border-platinum/20 text-platinum" data-testid="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-white/10">
                      <SelectItem value="client">Client - Investor</SelectItem>
                      <SelectItem value="referrer">Referrer - Network Builder</SelectItem>
                      <SelectItem value="partner">Partner - External approver</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Referral Code (Optional)</Label>
                  <Input
                    value={newUser.referral_code}
                    onChange={(e) => setNewUser(prev => ({ ...prev, referral_code: e.target.value.toUpperCase() }))}
                    placeholder="VC-ABC12345"
                    className="bg-white border-slate-200 text-slate-900 uppercase"
                    data-testid="new-user-referral"
                  />
                  <p className="text-gray-600 text-xs">Link to existing referrer</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">KYC Status</Label>
                  <Select 
                    value={newUser.kyc_status} 
                    onValueChange={(v) => setNewUser(prev => ({ ...prev, kyc_status: v }))}
                  >
                    <SelectTrigger className="bg-surface border-platinum/20 text-platinum">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-white/10">
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 rounded bg-obsidian border border-white/5">
                    <Label className="text-gray-300 text-sm">Registration Paid</Label>
                    <Switch
                      checked={newUser.registration_paid}
                      onCheckedChange={(v) => setNewUser(prev => ({ ...prev, registration_paid: v }))}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded bg-obsidian border border-white/5">
                    <Label className="text-gray-300 text-sm">Membership Active</Label>
                    <Switch
                      checked={newUser.membership_active}
                      onCheckedChange={(v) => setNewUser(prev => ({ ...prev, membership_active: v }))}
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gold hover:bg-gold/90 text-navy-dark"
                  disabled={creating || !user?.role}
                  data-testid="create-user-submit"
                >
                  {creating ? "Creating..." : "Create User"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Portal Address</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">KYC</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Payment</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Referral Code</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Joined</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-400">Active</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-white font-medium">
                          {u.full_name}
                          {u.marked_for_deletion && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-500/15 border border-rose-500/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-300 align-middle"
                              title={`Queued for deletion${u.deletion_reason ? `: ${u.deletion_reason}` : ""}${u.deletion_marked_by ? ` — by ${u.deletion_marked_by}` : ""}`}
                              data-testid={`queued-badge-${u.id}`}
                            >
                              <Trash2 size={10} /> Queued
                            </span>
                          )}
                        </p>
                        <p className="text-gray-500 text-sm">{u.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {u.internal_email ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => copyReferralCode(u.internal_email)}
                            className="flex items-center gap-2 hover:text-blue-600 transition-colors group"
                            data-testid={`internal-email-${u.id}`}
                            title="Copy portal address"
                          >
                            <code className="text-xs bg-blue-50 border border-blue-100 px-2 py-1 rounded text-blue-700 font-mono">
                              {u.internal_email}
                            </code>
                            {copied === u.internal_email ? (
                              <CheckCircle2 size={14} className="text-emerald-500" />
                            ) : (
                              <Copy size={14} className="text-slate-400 group-hover:text-blue-500" />
                            )}
                          </button>
                          {u.internal_email_provisioned ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]" data-testid={`mailbox-provisioned-${u.id}`}>
                              <CheckCircle2 size={10} className="mr-0.5" /> mailbox
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[10px]" data-testid={`mailbox-pending-${u.id}`}>
                              pending
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500 italic">not assigned</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={
                        u.role === "admin" ? "bg-gold/20 text-gold" :
                        u.role === "superuser" ? "bg-purple-500/20 text-purple-400" :
                        u.role === "referrer" ? "bg-blue-500/20 text-blue-400" :
                        u.role === "partner" ? "bg-teal-500/20 text-teal-400" :
                        "bg-gray-500/20 text-gray-400"
                      }>
                        {u.role}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Badge 
                        className={
                          u.kyc_status === "approved" ? "bg-emerald/20 text-emerald cursor-pointer" :
                          u.kyc_status === "pending" ? "bg-orange-500/20 text-orange-400 cursor-pointer" :
                          "bg-red-500/20 text-red-400 cursor-pointer"
                        }
                        onClick={() => updateKYCStatus(u.id, u.kyc_status === "approved" ? "pending" : "approved")}
                      >
                        {u.kyc_status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        {u.registration_paid && (
                          <Badge className="bg-emerald/20 text-emerald text-xs">Reg</Badge>
                        )}
                        {u.membership_active && (
                          <Badge className="bg-blue-500/20 text-blue-400 text-xs">Mem</Badge>
                        )}
                        {!u.registration_paid && !u.membership_active && (
                          <span className="text-gray-500 text-sm">None</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => copyReferralCode(u.referral_token)}
                        className="flex items-center gap-2 hover:text-gold transition-colors"
                      >
                        <code className="text-xs bg-white/5 px-2 py-1 rounded text-gold">{u.referral_token}</code>
                        {copied === u.referral_token ? (
                          <CheckCircle2 size={14} className="text-emerald" />
                        ) : (
                          <Copy size={14} className="text-gray-500" />
                        )}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-sm">{formatDate(u.created_at)}</td>
                    <td className="py-3 px-4 text-center">
                      <Switch
                        checked={u.is_active}
                        onCheckedChange={() => toggleUserStatus(u.id, u.is_active)}
                        disabled={!canManageLifecycle(u)}
                        data-testid={`toggle-${u.id}`}
                      />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`actions-${u.id}`}>
                            <MoreVertical size={16} className="text-gray-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-52 bg-white border-slate-200 shadow-xl shadow-slate-900/10"
                        >
                          <DropdownMenuItem
                            onClick={() => openEditDialog(u)}
                            className="text-slate-800 font-medium hover:bg-slate-100 focus:bg-slate-100 focus:text-slate-900 cursor-pointer"
                            data-testid={`edit-details-${u.id}`}
                          >
                            <Pencil size={14} className="mr-2 text-blue-400" />
                            Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-200" />
                          <DropdownMenuItem
                            onClick={() => openResetDialog(u)}
                            className="text-slate-800 font-medium hover:bg-slate-100 focus:bg-slate-100 focus:text-slate-900 cursor-pointer"
                          >
                            <KeyRound size={14} className="mr-2 text-orange-400" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-200" />
                          <DropdownMenuItem 
                            onClick={() => openRoleDialog(u)}
                            className="text-slate-800 font-medium hover:bg-slate-100 focus:bg-slate-100 focus:text-slate-900 cursor-pointer"
                          >
                            <Shield size={14} className="mr-2 text-purple-400" />
                            Change Role
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-200" />
                          <DropdownMenuItem 
                            onClick={() => openModuleAccessDialog(u)}
                            className="text-slate-800 font-medium hover:bg-slate-100 focus:bg-slate-100 focus:text-slate-900 cursor-pointer"
                            data-testid={`module-access-${u.id}`}
                          >
                            <Layers size={14} className="mr-2 text-blue-400" />
                            Module Access
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => openMailboxDialog(u)}
                            className="text-slate-800 font-medium hover:bg-slate-100 focus:bg-slate-100 focus:text-slate-900 cursor-pointer"
                            data-testid={`provision-mailbox-${u.id}`}
                            disabled={!u.internal_email}
                          >
                            <MailIcon size={14} className="mr-2 text-blue-400" />
                            {u.internal_email_provisioned ? "Mailbox ✓ (re-open)" : "Provision Mailbox"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-200" />
                          <DropdownMenuItem 
                            onClick={() => handleMarkQualified(u)}
                            className="text-slate-800 font-medium hover:bg-slate-100 focus:bg-slate-100 focus:text-slate-900 cursor-pointer"
                            data-testid={`mark-qualified-${u.id}`}
                            disabled={u.qualification_status === "qualified"}
                          >
                            <CheckCircle2 size={14} className="mr-2 text-emerald-400" />
                            {u.qualification_status === "qualified" ? "Already Qualified" : "Mark as Qualified"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-200" />
                          <DropdownMenuItem
                            onClick={() => toggleDeletionFlag(u)}
                            className="text-rose-700 font-medium hover:bg-rose-50 focus:bg-rose-50 focus:text-rose-800 cursor-pointer"
                            data-testid={`deletion-flag-${u.id}`}
                            disabled={!canManageLifecycle(u)}
                          >
                            <Trash2 size={14} className="mr-2 text-rose-600" />
                            {u.marked_for_deletion ? "Unmark for deletion" : "Mark for deletion"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Reset Password Dialog — 2FA flow */}
      <Dialog open={showResetDialog} onOpenChange={(o) => {
        if (!o) {
          setShowResetDialog(false);
          setResetStage("init");
          setResetCode("");
          setResetToken("");
          setNewPassword("");
        }
      }}>
        <DialogContent className="bg-white border-slate-200 max-w-md" aria-describedby="reset-password-description">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-serif flex items-center gap-2">
              <KeyRound className="text-blue-600" size={20} />
              Reset Password (2FA)
            </DialogTitle>
            <p id="reset-password-description" className="text-slate-600 text-sm">
              {resetStage === "init"
                ? "We'll send a 6-digit code to your email and SMS for confirmation."
                : `Enter the code sent via ${resetChannels.join(", ")} and choose a new password.`}
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 rounded bg-blue-50 border border-blue-200">
              <p className="text-blue-900 text-sm">
                <strong>User:</strong> {resetUser?.full_name}
              </p>
              <p className="text-blue-700/80 text-xs mt-1">{resetUser?.email}</p>
            </div>

            {resetStage === "init" ? (
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowResetDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleResetRequest}
                  disabled={resetting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  data-testid="send-2fa-code"
                >
                  {resetting ? "Sending…" : "Send 2FA code"}
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label className="text-slate-700">6-digit code</Label>
                  <Input
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    inputMode="numeric"
                    className="bg-white border-slate-200 text-slate-900 font-mono tracking-widest text-center text-lg"
                    data-testid="reset-2fa-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">New Password</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      className="bg-white border-slate-200 text-slate-900 pr-10"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowResetDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleResetPassword}
                    disabled={resetting || !newPassword || newPassword.length < 8 || resetCode.length !== 6}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                    data-testid="confirm-reset-password"
                  >
                    {resetting ? "Verifying…" : "Confirm reset"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="bg-surface border-white/10 max-w-md" aria-describedby="change-role-description">
          <DialogHeader>
            <DialogTitle className="text-white font-serif flex items-center gap-2">
              <Shield className="text-purple-400" size={20} />
              Change User Role
            </DialogTitle>
            <p id="change-role-description" className="text-gray-400 text-sm">
              Update role for {roleUser?.email}
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 rounded bg-purple-500/10 border border-purple-500/30">
              <p className="text-purple-300 text-sm">
                <strong>User:</strong> {roleUser?.full_name}
              </p>
              <p className="text-purple-300/70 text-xs mt-1">
                Current role: <Badge className="ml-1 bg-white/10">{roleUser?.role}</Badge>
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">New Role *</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="bg-surface border-platinum/20 text-platinum" data-testid="role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10">
                  <SelectItem value="client">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      Client - Standard user
                    </div>
                  </SelectItem>
                  <SelectItem value="referrer">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                      Referrer - Network builder
                    </div>
                  </SelectItem>
                  <SelectItem value="partner">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-teal-400"></div>
                      Partner - External approver
                    </div>
                  </SelectItem>
                  <SelectItem value="superuser">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                      Superuser - Full access
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-gold"></div>
                      Admin - System administrator
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-gray-600 text-xs">
                Superuser and Admin have full management capabilities
              </p>
            </div>

            {selectedRole !== roleUser?.role && (
              <div className="p-3 rounded bg-amber-500/10 border border-amber-500/30">
                <p className="text-amber-300 text-xs">
                  ⚠️ Changing role from <strong>{roleUser?.role}</strong> to <strong>{selectedRole}</strong> will immediately update their access permissions.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setShowRoleDialog(false)}
                className="flex-1 border-white/10 text-platinum"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRoleChange}
                disabled={changingRole || !selectedRole || selectedRole === roleUser?.role}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                data-testid="confirm-role-change"
              >
                {changingRole ? "Updating..." : "Update Role"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Details Dialog — admin correction of name / phone / email (forge-20260616-003) */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-surface border-white/10 max-w-md" aria-describedby="edit-details-description">
          <DialogHeader>
            <DialogTitle className="text-white font-serif flex items-center gap-2">
              <Pencil className="text-blue-400" size={20} />
              Edit User Details
            </DialogTitle>
            <p id="edit-details-description" className="text-gray-400 text-sm">
              Correct the name, phone, or email for this account.
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Full Name *</Label>
              <Input
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                className="bg-surface border-platinum/20 text-platinum"
                data-testid="edit-full-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Phone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="bg-surface border-platinum/20 text-platinum"
                data-testid="edit-phone"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                disabled={editUser?.role === "superuser" || !canManageLifecycle(editUser || {})}
                className="bg-surface border-platinum/20 text-platinum disabled:opacity-50"
                data-testid="edit-email"
              />
              {editUser?.role === "superuser" && (
                <p className="text-gray-600 text-xs">Superuser email is reseeded at boot and can't be changed here.</p>
              )}
              {editUser?.role !== "superuser" && !canManageLifecycle(editUser || {}) && (
                <p className="text-gray-600 text-xs">Only a superuser can change the email of an admin, superuser, or partner account.</p>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1 border-white/10 text-platinum">
                Cancel
              </Button>
              <Button
                onClick={saveProfileEdit}
                disabled={savingEdit || !editForm.full_name.trim()}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="confirm-edit-details"
              >
                {savingEdit ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Module Access Dialog (superuser only — gated inside the component) */}
      <ModuleAccessDialog
        open={moduleAccessOpen}
        onOpenChange={(v) => { setModuleAccessOpen(v); if (!v) setModuleAccessUser(null); }}
        targetUser={moduleAccessUser}
      />

      {/* Mailbox Provisioning Dialog */}
      <ProvisionMailboxDialog
        open={mailboxOpen}
        onOpenChange={(v) => {
          setMailboxOpen(v);
          if (!v) {
            setMailboxUserId(null);
            fetchUsers();  // refresh the pending/provisioned chip
          }
        }}
        userId={mailboxUserId}
      />
    </DashboardLayout>
  );
};

export default UsersPage;
